import React from "react";
import { useCart } from "../context/CartContext";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { LuShoppingCart, LuArrowRight, LuArrowLeft, LuCheck, LuTrendingUp, LuLock, LuTruck, LuPackage } from "react-icons/lu";

/* ─── Bulk tier configuration ─────────────────────────────── */
const TIERS = [
  { label: "Standard Retail", threshold: 0,    discount: 0,    tag: "MSRP" },
  { label: "Bulk (Tier 1)",   threshold: 200,  discount: 0.05, tag: "-5%"  },
  { label: "Bulk (Tier 2)",   threshold: 500,  discount: 0.12, tag: "-12%" },
  { label: "Institutional",   threshold: 1000, discount: 0.18, tag: "-18%" },
];

function getCurrentTier(total) {
  let tier = TIERS[0];
  for (const t of TIERS) { if (total >= t.threshold) tier = t; }
  return tier;
}
function getNextTier(total) {
  for (const t of TIERS) { if (total < t.threshold) return t; }
  return null;
}

export default function CartPage() {
  const { cart, removeFromCart, getTotalPrice, addToCart, decreaseQuantity, clearCart } = useCart();
  const navigate = useNavigate();

  const rows = cart;

  const subtotal    = getTotalPrice();
  const currentTier = getCurrentTier(subtotal);
  const nextTier    = getNextTier(subtotal);
  const discount    = subtotal * currentTier.discount;
  const afterDiscount = subtotal - discount;
  const shipping    = afterDiscount >= 500 ? 0 : afterDiscount > 0 ? "Calc. at checkout" : 0;
  const shippingNum = typeof shipping === "number" ? shipping : 0;
  const total       = afterDiscount + shippingNum;

  const tierProgress = nextTier
    ? Math.min(100, ((subtotal - currentTier.threshold) / (nextTier.threshold - currentTier.threshold)) * 100)
    : 100;

  /* ── Empty state ─────────────────────────────────────────── */
  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-background font-sans flex items-center justify-center px-6">
        <div className="text-center max-w-sm">
          <div className="w-20 h-20 rounded-3xl bg-card border border-border shadow-sm flex items-center justify-center mx-auto mb-6">
            <LuShoppingCart size={36} className="text-[var(--color-muted-foreground)] opacity-50" />
          </div>
          <h2 className="text-2xl font-black text-foreground mb-2">Your cart is empty</h2>
          <p className="text-[var(--color-muted-foreground)] text-sm mb-8">Add wholesale products to get started on your order.</p>
          <Link to="/products/disposable-vapes" className="inline-flex items-center gap-2 bg-brand-navy hover:bg-brand-amber transition-colors text-white font-bold px-8 py-3.5 rounded-xl">
            Browse Products
            <LuArrowRight size={16} />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background font-sans">
      <div className="max-w-screen-xl mx-auto px-6 py-12 pt-32">

        {/* ── Page heading ─────────────────────────────────── */}
        <div className="mb-10">
          <h1 className="text-4xl font-extrabold tracking-tight text-foreground mb-2">Order Review</h1>
          <p className="text-[var(--color-muted-foreground)] font-medium flex items-center gap-2 text-sm">
            <LuCheck size={16} className="text-brand-amber" />
            Authenticated B2B Wholesale Account
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">

          {/* ── Left: items ──────────────────────────────────── */}
          <div className="lg:col-span-8 space-y-6">

            {/* Bulk discount alert */}
            <div className="bg-blue-50 border border-blue-100 dark:bg-blue-950/30 dark:border-blue-900/50 p-5 rounded-xl flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="bg-brand-amber p-2.5 rounded-lg flex-shrink-0">
                  <LuTrendingUp size={20} className="text-white" />
                </div>
                <div>
                  {currentTier.discount > 0 ? (
                    <h3 className="text-foreground font-bold text-sm">
                      Bulk Discount Applied: {Math.round(currentTier.discount * 100)}% — saving ${discount.toFixed(2)}
                    </h3>
                  ) : (
                    <h3 className="text-foreground font-bold text-sm">No discount tier active yet</h3>
                  )}
                  {nextTier ? (
                    <p className="text-[var(--color-muted-foreground)] text-xs mt-0.5">
                      Add <span className="text-foreground font-bold">${(nextTier.threshold - subtotal).toFixed(2)}</span> more to unlock the {Math.round(nextTier.discount * 100)}% {nextTier.label} tier.
                    </p>
                  ) : (
                    <p className="text-green-600 dark:text-green-400 text-xs mt-0.5 font-medium">Max discount tier reached!</p>
                  )}
                </div>
              </div>
              <Link to="/products/disposable-vapes" className="text-brand-amber text-xs font-bold tracking-widest uppercase hover:underline flex-shrink-0 hidden sm:block">
                Add More →
              </Link>
            </div>

            {/* Table header */}
            <div className="hidden md:grid grid-cols-12 px-6 py-3 text-xs font-bold uppercase tracking-widest text-[var(--color-muted-foreground)]">
              <div className="col-span-6">Product Details</div>
              <div className="col-span-2 text-center">Unit Price</div>
              <div className="col-span-2 text-center">Quantity</div>
              <div className="col-span-2 text-right">Line Total</div>
            </div>

            {/* Product rows */}
            <div className="space-y-3">
              {rows.map((item) => {
                const unitPrice = item.price || 0;
                const lineTotal = unitPrice * (item.quantity || 0);
                const cartKey   = item.cartKey || item.id;

                return (
                  <div key={cartKey} className="group grid grid-cols-12 items-center bg-card p-5 rounded-xl hover:bg-muted transition-all duration-300 border border-border">
                    {/* Product info */}
                    <div className="col-span-12 md:col-span-6 flex gap-4 mb-3 md:mb-0">
                      <div className="w-20 h-20 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {(() => {
                          const src = item.imageUrls?.[0] || item.primaryImageUrl || item.imageUrl;
                          return src
                            ? <img src={src} alt={item.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                            : <LuPackage size={32} className="text-[var(--color-muted-foreground)]" />;
                        })()}
                      </div>
                      <div className="flex flex-col justify-center min-w-0">
                        {item.category && <span className="text-xs font-bold text-brand-amber uppercase tracking-widest mb-1">{item.category}</span>}
                        <h4 className="font-bold text-foreground leading-tight truncate">{item.name}</h4>
                        {item.selectedVariant && (
                          <p className="text-xs text-[var(--color-muted-foreground)] font-mono uppercase mt-0.5">{item.selectedVariant}</p>
                        )}
                        <button
                          onClick={() => { removeFromCart(cartKey); toast.info("Item removed"); }}
                          className="text-xs text-[var(--color-muted-foreground)] hover:text-red-500 transition-colors mt-1.5 text-left w-fit"
                        >
                          Remove
                        </button>
                      </div>
                    </div>

                    {/* Unit price */}
                    <div className="col-span-4 md:col-span-2 text-center">
                      <span className="font-bold text-foreground">${unitPrice.toFixed(2)}</span>
                      <p className="text-[10px] text-[var(--color-muted-foreground)] uppercase">per unit</p>
                    </div>

                    {/* Qty stepper */}
                    <div className="col-span-4 md:col-span-2 flex justify-center">
                      <div className="flex items-center bg-accent/10 rounded-lg p-1 gap-1">
                        <button
                          onClick={() => decreaseQuantity(cartKey)}
                          className="w-7 h-7 flex items-center justify-center text-foreground hover:text-accent transition-colors font-bold"
                        >
                          −
                        </button>
                        <span className="w-10 text-center font-bold text-foreground text-sm">{item.quantity}</span>
                        <button
                          onClick={() => addToCart(item, 1)}
                          className="w-7 h-7 flex items-center justify-center text-foreground hover:text-accent transition-colors font-bold"
                        >
                          +
                        </button>
                      </div>
                    </div>

                    {/* Line total */}
                    <div className="col-span-4 md:col-span-2 text-right">
                      <span className="text-lg font-bold text-foreground">${lineTotal.toFixed(2)}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Quote actions bar */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 py-6">
              <Link to="/products/disposable-vapes" className="flex items-center gap-2 text-foreground font-bold hover:text-accent transition-colors group">
                <LuArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
                Continue Catalog Browsing
              </Link>
              <div className="flex gap-3">
                <button
                  onClick={() => { clearCart(); toast.success("Cart cleared!"); }}
                  className="px-5 py-2.5 border border-border text-foreground font-bold rounded-lg hover:bg-muted transition-colors text-sm"
                >
                  Clear Cart
                </button>
                <button
                  onClick={() => toast.info("Quantities already up to date.")}
                  className="px-5 py-2.5 bg-muted text-foreground font-bold rounded-lg hover:opacity-80 transition-colors text-sm border border-border"
                >
                  Update Quantities
                </button>
              </div>
            </div>
          </div>

          {/* ── Right: summary sidebar ───────────────────────── */}
          <div className="lg:col-span-4 sticky top-28 self-start">
            <div className="bg-accent/5 dark:bg-accent/10 border border-accent/10 rounded-2xl p-7 space-y-6">
              <h2 className="text-2xl font-extrabold text-foreground tracking-tight">Order Summary</h2>

              {/* Line items */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-[var(--color-muted-foreground)] font-medium text-sm">Subtotal ({cart.reduce((s,i) => s + i.quantity, 0)} items)</span>
                  <span className="text-foreground font-bold font-mono">${subtotal.toFixed(2)}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-[var(--color-muted-foreground)] font-medium text-sm">Bulk Discount ({currentTier.label})</span>
                    <span className="text-brand-amber font-bold font-mono">−${discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-[var(--color-muted-foreground)] font-medium text-sm">Estimated Shipping</span>
                  <span className="text-foreground font-bold font-mono text-sm">
                    {typeof shipping === "number" && shipping === 0 ? "Free" : shipping}
                  </span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-border">
                  <span className="text-[var(--color-muted-foreground)] font-medium text-sm">Tax (Calculated at checkout)</span>
                  <span className="text-foreground font-bold font-mono">$0.00</span>
                </div>
                <div className="flex justify-between items-center pt-1">
                  <span className="text-xl font-extrabold text-foreground">Total Amount</span>
                  <span className="text-2xl font-extrabold text-foreground font-mono">${total.toFixed(2)}</span>
                </div>
              </div>

              {/* CTAs */}
              <div className="space-y-3">
                <Link
                  to="/checkout"
                  className="w-full bg-brand-amber text-brand-navy py-4 rounded-xl font-extrabold text-lg flex items-center justify-center gap-3 hover:opacity-90 transition-all shadow-lg shadow-brand-amber/20 active:scale-[0.98]"
                >
                  Proceed to Checkout
                  <LuLock size={20} />
                </Link>

                {/* Logistics guarantee */}
                <div className="p-4 bg-card/60 rounded-xl border border-border flex gap-3 items-start">
                  <LuTruck size={20} className="text-brand-amber flex-shrink-0 mt-0.5" />
                  <div className="text-xs">
                    <p className="font-bold text-foreground mb-1">Wholesale Logistics Guarantee</p>
                    <p className="text-[var(--color-muted-foreground)] leading-relaxed">Fast wholesale fulfillment on all bulk orders. Expedited options available at checkout.</p>
                  </div>
                </div>

                <button className="w-full text-foreground font-bold text-xs py-2 hover:underline tracking-widest uppercase">
                  Request Official Quote PDF
                </button>
              </div>
            </div>

            {/* Volume Tiers widget */}
            <div className="mt-4 bg-accent/10 dark:bg-accent/5 border border-accent/10 p-6 rounded-2xl">
              <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--color-muted-foreground)] mb-4">Volume Tiers</h4>
              <div className="space-y-2.5">
                {TIERS.map((tier, i) => {
                  const isActive  = tier.label === currentTier.label;
                  const isPast    = tier.threshold < currentTier.threshold;
                  const isLocked  = tier.threshold > subtotal;
                  return (
                    <div key={i} className={`flex justify-between text-xs ${isPast ? "opacity-40 line-through" : ""} ${isActive ? "font-bold text-brand-amber" : "font-medium text-foreground"}`}>
                      <span>{isActive ? `Active: ${tier.label}` : tier.label}</span>
                      {tier.discount === 0 ? (
                        <span className="text-[var(--color-muted-foreground)]">MSRP</span>
                      ) : isActive ? (
                        <span>{tier.tag}</span>
                      ) : isLocked ? (
                        <span className="bg-muted text-[var(--color-muted-foreground)] px-2 py-0.5 rounded">{tier.tag}</span>
                      ) : (
                        <span className="text-green-600 dark:text-green-400">{tier.tag}</span>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="h-1.5 w-full bg-card rounded-full overflow-hidden mt-4">
                <div
                  className="h-full bg-brand-amber rounded-full transition-all duration-700"
                  style={{ width: `${tierProgress}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
